import React from "react";
import { useEditorStore } from "../../../store";
import { useShallow } from "zustand/react/shallow";
import { DebouncedTextField } from "../../common/DebouncedTextField";

export const ComponentStylePanel: React.FC = () => {
  const selectedComponentId = useEditorStore(
    (state) => state.ui.selectedComponentId
  );

  const componentStyles = useEditorStore(
    useShallow((state) => {
      const components =
        (state.config.design.settings
          .components as import("../../../store").ComponentSettings) || {};

      if (!selectedComponentId) return {};
      // Typed safe access
      return components[selectedComponentId as keyof typeof components] || {};
    })
  );

  const updateComponentStyle = useEditorStore(
    (state) => state.updateComponentStyle
  );

  const handleUpdate = (key: string, value: unknown) => {
    if (!selectedComponentId) return;
    updateComponentStyle(selectedComponentId, key, value);
  };

  const renderContent = () => {
    if (!selectedComponentId) {
      return (
        <s-box padding="base">
          <s-text color="subdued">
            Select a component to edit its styles.
          </s-text>
        </s-box>
      );
    }

    switch (selectedComponentId) {
      case "shipping_method": {
        const styles =
          componentStyles as import("../../../store").ComponentSettings["shipping_method"];
        return (
          <s-stack gap="base" direction="block">
            <s-text color="subdued">
              Cambia al modo vista previa en la parte superior para interactuar
              con las opciones de entrega.
            </s-text>

            {/* <s-divider />

            <s-text type="strong">Colors</s-text>

            <s-color-field
              label="Background color"
              name="background_color"
              value={styles?.background_color || "#FFFFFF"}
              onInput={(event) =>
                handleUpdate("background_color", event.currentTarget.value)
              }
            />

            <s-text-field
              label="Text color"
              name="text_color"
              value={styles?.text_color || "#111827"}
              onInput={(event) =>
                handleUpdate("text_color", event.currentTarget.value)
              }
            />

            <s-color-field
              label="Active step color"
              name="active_step_color"
              value={styles?.active_step_color || "#008060"}
              onInput={(event) =>
                handleUpdate("active_step_color", event.currentTarget.value)
              }
            /> */}

            <s-text type="strong">Opciones</s-text>

            <s-select
              label="Mostrar botón de opción"
              name="show_radio_button"
              value={(styles?.show_radio_button ?? true) ? "true" : "false"}
              onChange={(event) =>
                handleUpdate(
                  "show_radio_button",
                  event.currentTarget.value === "true"
                )
              }
            >
              <s-option value="true">Sí</s-option>
              <s-option value="false">No</s-option>
            </s-select>
          </s-stack>
        );
      }

      case "order_summary": {
        return (
          <s-box padding="base">
            <s-text color="subdued">
              Configura las opciones del Resumen de Orden en el panel de
              configuración del campo (panel derecho al seleccionar el campo).
            </s-text>
          </s-box>
        );
      }

      case "submit_button": {
        const styles =
          componentStyles as import("../../../store").ComponentSettings["submit_button"];
        // Also retrieve global fallbacks to show as placeholders or hints
        const designSettings = useEditorStore.getState().config.design.settings;

        return (
          <s-stack gap="base" direction="block">
            <s-text type="strong">Content</s-text>

            <DebouncedTextField
              label="Button Text"
              name="text"
              value={styles?.text || ""}
              placeholder={
                designSettings?.submit_button_text || "Comprar - $100,000"
              }
              onInput={(event) =>
                handleUpdate("text", event.currentTarget.value)
              }
            />

            <s-divider />

            <s-text type="strong">Colors & Border</s-text>

            <s-color-field
              label="Background color"
              name="background_color"
              value={
                styles?.background_color ||
                designSettings?.primary_color ||
                "#008060"
              }
              onInput={(event) =>
                handleUpdate("background_color", event.currentTarget.value)
              }
            />

            <s-text-field
              label="Text color"
              name="text_color"
              value={styles?.text_color || "#FFFFFF"}
              onInput={(event) =>
                handleUpdate("text_color", event.currentTarget.value)
              }
            />

            <div>
              <div className="flex justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">
                  Corner Radius
                </label>
                <span className="text-xs text-gray-500">
                  {styles?.border_radius ?? designSettings?.corner_radius ?? 4}
                  px
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                value={
                  styles?.border_radius ?? designSettings?.corner_radius ?? 4
                }
                onChange={(e) =>
                  handleUpdate("border_radius", parseFloat(e.target.value))
                }
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
            </div>
          </s-stack>
        );
      }

      default:
        return (
          <s-text color="subdued">
            No visual settings available for this component.
          </s-text>
        );
    }
  };

  return (
    <s-box padding="base">
      <s-stack gap="large-400" direction="block">
        {renderContent()}
      </s-stack>
    </s-box>
  );
};
